/** Generates an RFC4122 v4 UUID safely across secure and insecure HTTP contexts. */
export function randomId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") {
    try {
      return c.randomUUID();
    } catch {
      // fallback
    }
  }
  if (c && typeof c.getRandomValues === "function") {
    try {
      const bytes = new Uint8Array(16);
      c.getRandomValues(bytes);
      bytes[6] = (bytes[6]! & 0x0f) | 0x40;
      bytes[8] = (bytes[8]! & 0x3f) | 0x80;
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    } catch {
      // fallback
    }
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** `crypto.randomUUID` is missing on insecure HTTP (LAN IPs). Polyfill once at boot. */
export function ensureRandomUUID(): void {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return;

  // 1. Try prototype of Crypto interface if available
  try {
    const proto = (globalThis as unknown as { Crypto?: { prototype?: { randomUUID?: unknown } } }).Crypto?.prototype;
    if (proto && typeof proto.randomUUID !== "function") {
      Object.defineProperty(proto, "randomUUID", {
        configurable: true,
        writable: true,
        value: randomId,
      });
    }
  } catch {
    // ignore
  }

  // 2. Try directly on crypto instance
  try {
    if (c && typeof c.randomUUID !== "function") {
      Object.defineProperty(c, "randomUUID", {
        configurable: true,
        writable: true,
        value: randomId,
      });
    }
  } catch {
    // ignore
  }

  // 3. Fallback: if crypto does not exist, define it on globalThis
  try {
    if (!globalThis.crypto) {
      Object.defineProperty(globalThis, "crypto", {
        configurable: true,
        writable: true,
        value: {
          randomUUID: randomId,
          getRandomValues: (arr: Uint8Array) => {
            for (let i = 0; i < arr.length; i++) arr[i] = Math.floor(Math.random() * 256);
            return arr;
          },
        },
      });
    }
  } catch {
    // ignore
  }
}

